/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { forwardRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import Chip from "@mui/material/Chip";

// Custom styles for MDAutocomplete
import MDAutocompleteRoot from "components/MDAutocomplete/MDAutocompleteRoot";

const MDAutocomplete = forwardRef(
  (
    {
      multiple = false,
      enableSelectAll = true,
      selectAllLabel = "Select All",
      options = [],
      value,
      onChange,
      renderOption,
      renderTags,
      getOptionDisabled,
      getOptionLabel,
      isOptionEqualToValue,
      ...rest
    },
    ref
  ) => {
    // Select All constants
    const SELECT_ALL_ID = "__select_all__";
    const OLD_SELECT_ALL_ID = "all";

    // Helper function to get option identifier (id or value)
    const getOptionId = useCallback((opt) => {
      if (!opt) return null;
      return opt.id ?? opt.value;
    }, []);

    // Helper function to get selectable options (exclude disabled and Select All options)
    const getSelectableOptions = useCallback(
      (opts) => {
        if (!opts || !Array.isArray(opts)) return [];
        return opts.filter((opt) => {
          if (!opt) return false;
          const optId = getOptionId(opt);
          return (
            optId !== SELECT_ALL_ID && optId !== OLD_SELECT_ALL_ID && !getOptionDisabled?.(opt)
          );
        });
      },
      [SELECT_ALL_ID, OLD_SELECT_ALL_ID, getOptionDisabled, getOptionId]
    );

    // Helper function to check if all selectable options are selected
    const areAllSelected = useCallback(
      (currentValue, allOptions) => {
        const selectable = getSelectableOptions(allOptions);
        if (!selectable.length) return false;
        if (!currentValue || !Array.isArray(currentValue)) return false;
        return selectable.every((opt) =>
          currentValue.some((val) => {
            // Handle both object and primitive comparisons
            if (
              typeof val === "object" &&
              val !== null &&
              typeof opt === "object" &&
              opt !== null
            ) {
              const valId = getOptionId(val);
              const optId = getOptionId(opt);
              return valId === optId;
            }
            return val === opt;
          })
        );
      },
      [getSelectableOptions, getOptionId]
    );

    // Default getOptionLabel that works with both id/value and label properties
    const defaultGetOptionLabel = useCallback((option) => {
      if (!option) return "";
      if (typeof option === "string") return option;
      return option.label || option.name || String(option.id ?? option.value ?? option);
    }, []);

    // Default isOptionEqualToValue that works with both id/value properties
    const defaultIsOptionEqualToValue = useCallback(
      (option, value) => {
        if (!option || !value) return option === value;
        const optionId = getOptionId(option);
        const valueId = getOptionId(value);
        return optionId === valueId;
      },
      [getOptionId]
    );

    // Determine if Select All should be shown
    const shouldShowSelectAll = multiple && enableSelectAll && options.length > 0;

    // Prepare options with Select All if needed
    const optionsWithSelectAll = useMemo(() => {
      if (!shouldShowSelectAll) return options;
      return [{ id: SELECT_ALL_ID, label: selectAllLabel }, ...options];
    }, [shouldShowSelectAll, options, SELECT_ALL_ID, selectAllLabel]);

    // Compute the display value for chips/tags (never include Select All)
    const displayValue = useMemo(() => {
      if (!multiple) return value;

      const currentValue = value || [];

      // Filter out any Select All options from the value
      // This ensures Select All never appears as a chip
      const filteredValue = currentValue.filter((option) => {
        const optId = getOptionId(option);
        return (
          optId !== SELECT_ALL_ID &&
          optId !== OLD_SELECT_ALL_ID &&
          option !== SELECT_ALL_ID &&
          option !== OLD_SELECT_ALL_ID
        );
      });

      return filteredValue;
    }, [multiple, value, getOptionId, SELECT_ALL_ID, OLD_SELECT_ALL_ID]);

    // Compute value for dropdown checkboxes (include Select All if all are selected)
    const dropdownValue = useMemo(() => {
      if (!multiple) return value;

      const currentValue = value || [];

      // If all selectable options are selected, add Select All to show it as checked
      if (shouldShowSelectAll && areAllSelected(currentValue, options)) {
        return [{ id: SELECT_ALL_ID, label: selectAllLabel }, ...currentValue];
      }

      return currentValue;
    }, [
      multiple,
      value,
      shouldShowSelectAll,
      areAllSelected,
      options,
      SELECT_ALL_ID,
      selectAllLabel,
    ]);

    // Enhanced onChange handler for Select All logic
    const handleChange = useCallback(
      (event, newValue, reason, details) => {
        if (!multiple) {
          // Single select - just pass through
          if (onChange) {
            onChange(event, newValue, reason, details);
          }
          return;
        }

        // Handle multiple selection with Select All logic
        const newValueArray = newValue || [];
        const previousValue = value || [];
        const wasAllSelected = areAllSelected(previousValue, options);

        // Check if the user actually clicked on the Select All option
        const clickedSelectAll =
          (reason === "selectOption" || reason === "removeOption") &&
          details?.option?.id === SELECT_ALL_ID;

        let processedValue;

        if (clickedSelectAll) {
          // User specifically clicked on Select All option
          if (wasAllSelected) {
            // If all were selected, deselect all
            processedValue = [];
          } else {
            // Otherwise, select all selectable options
            processedValue = getSelectableOptions(options);
          }
        } else {
          // Normal selection - filter out Select All pseudo-options
          processedValue = newValueArray.filter((v) => {
            const vId = getOptionId(v);
            return (
              vId !== SELECT_ALL_ID &&
              vId !== OLD_SELECT_ALL_ID &&
              v !== SELECT_ALL_ID &&
              v !== OLD_SELECT_ALL_ID
            );
          });
        }

        // Call parent's onChange with the processed value
        if (onChange) {
          onChange(event, processedValue, reason, details);
        }
      },
      [
        multiple,
        value,
        areAllSelected,
        options,
        getSelectableOptions,
        onChange,
        SELECT_ALL_ID,
        OLD_SELECT_ALL_ID,
        getOptionId,
      ]
    );

    // Enhanced renderOption to style Select All
    const enhancedRenderOption = useCallback(
      (props, option, state) => {
        const optionId = getOptionId(option);
        if (optionId === SELECT_ALL_ID) {
          return (
            <li
              {...props}
              key={SELECT_ALL_ID}
              style={{
                fontWeight: 600,
                borderBottom: "1px solid #e0e0e0",
                ...props.style,
              }}
            >
              {option.label}
            </li>
          );
        }

        // Use custom renderOption if provided
        if (renderOption) {
          return renderOption(props, option, state);
        }

        // Default rendering
        const label = option?.label || option;
        return (
          <li {...props} key={optionId || option}>
            {label}
          </li>
        );
      },
      [SELECT_ALL_ID, renderOption, getOptionId]
    );

    // Enhanced renderTags to exclude Select All from chips
    const enhancedRenderTags = useCallback(
      (selected, getTagProps, ownerState) => {
        // Create a map of original indices for non-Select All options
        const originalIndices = [];
        const filteredSelected = [];
        
        selected.forEach((option, originalIndex) => {
          const optId = getOptionId(option);
          const isSelectAll = 
            optId === SELECT_ALL_ID ||
            optId === OLD_SELECT_ALL_ID ||
            option === SELECT_ALL_ID ||
            option === OLD_SELECT_ALL_ID;
          
          if (!isSelectAll) {
            filteredSelected.push(option);
            originalIndices.push(originalIndex);
          }
        });

        // Use custom renderTags if provided
        if (renderTags) {
          // Create a wrapper for getTagProps that maps to original indices
          const wrappedGetTagProps = ({ index }) => {
            const originalIndex = originalIndices[index];
            return getTagProps({ index: originalIndex });
          };
          return renderTags(filteredSelected, wrappedGetTagProps, ownerState);
        }

        // Render chips manually for filtered selection
        // Use original indices to ensure correct removal
        return filteredSelected.map((option, filteredIndex) => {
          const label = defaultGetOptionLabel(option);
          const originalIndex = originalIndices[filteredIndex];
          const { key, className, ...chipProps } = getTagProps({ index: originalIndex });
          
          return (
            <Chip
              key={key}
              label={label}
              size="small"
              color="primary"
              variant="soft"
              {...chipProps}
            />
          );
        });
      },
      [SELECT_ALL_ID, OLD_SELECT_ALL_ID, renderTags, getOptionId, defaultGetOptionLabel]
    );

    // ALWAYS use enhancedRenderTags in multiple mode to filter Select All from chips
    // Even if no custom renderTags, we need to filter out Select All
    const renderTagsProp = multiple ? enhancedRenderTags : renderTags;

    return (
      <MDAutocompleteRoot
        {...rest}
        ref={ref}
        multiple={multiple}
        options={optionsWithSelectAll}
        value={multiple ? dropdownValue : value}
        onChange={handleChange}
        renderOption={enhancedRenderOption}
        {...(renderTagsProp && { renderTags: renderTagsProp })}
        getOptionDisabled={getOptionDisabled}
        getOptionLabel={getOptionLabel || defaultGetOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue || defaultIsOptionEqualToValue}
        sx={{
          '& .MuiAutocomplete-tag': {
            margin: '2px 4px',
          },
          '& .MuiChip-root': {
            margin: '2px',
          },
          '& .MuiAutocomplete-tag': {
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '16px',
            fontSize: '0.8125rem',
            fontWeight: 500,
            margin: '2px',
          },
          ...rest.sx,
        }}
      />
    );
  }
);

MDAutocomplete.displayName = "MDAutocomplete";

MDAutocomplete.propTypes = {
  multiple: PropTypes.bool,
  enableSelectAll: PropTypes.bool,
  selectAllLabel: PropTypes.string,
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
  renderOption: PropTypes.func,
  renderTags: PropTypes.func,
  getOptionDisabled: PropTypes.func,
  getOptionLabel: PropTypes.func,
  isOptionEqualToValue: PropTypes.func,
};

export default MDAutocomplete;
